import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register: doRegister } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await doRegister(email, password, name);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <div className="pt-28 pb-16 px-6 flex items-center justify-center">
        <Card className="w-full max-w-md rounded-2xl shadow-floating border-0" data-testid="register-card">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Heart className="h-8 w-8 text-[#4FB3D4] mx-auto mb-3" />
              <h1 className="font-heading text-2xl font-bold text-[#1A2A3A]" data-testid="register-title">Join ThryvLife</h1>
              <p className="text-sm text-[#1A2A3A]/60">Create your account and start healing</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A2A3A] mb-1">Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" placeholder="Your name" data-testid="register-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2A3A] mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" placeholder="you@example.com" data-testid="register-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2A3A] mb-1">Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" placeholder="At least 6 characters" data-testid="register-password" />
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="register-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full py-5 font-semibold" data-testid="register-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Account"}
              </Button>
            </form>
            <p className="text-center text-sm text-[#1A2A3A]/60 mt-5">
              Already have an account? <Link to="/login" className="text-[#4FB3D4] font-medium hover:underline" data-testid="login-link">Sign in</Link>
            </p>
            <p className="text-center text-[10px] text-[#1A2A3A]/40 mt-3">By creating an account, you agree that ThryvLife does not diagnose, treat, or cure any medical or mental health condition.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
